import { Colors } from '../../../models/disciplines';
import { PlayerType } from '../../../models/video';
import { TagProps } from '../../atoms/Tag';
import Timeline from '../../atoms/Timeline';
import { Typography } from '../../molecules/Typography';
import { Button } from '../../atoms/Button';
import { BreadcrumbsItem } from '../Breadcrumbs';
import { ButtonGroup } from '../ButtonGroup';
import Comment from '../comment';
import { PopUpModal } from '../../../../modules/Gamification/components/Playing/PopUpModal';
import { cnBase } from 'tailwind-variants';
import { TagsFiltersLesson } from './components/tags-filters';
import NextVideo from './components/next-video';
import LessonHeader from './components/lesson-header';
import LessonPlayer from './components/lesson-player';
import RelatedContent from './components/related-content';
import LessonFlashcards from './components/lesson-flashcards';
import { useLessonPage } from './hooks/useLessonPage';
import { useLessonNotesPdf } from './hooks/useLessonNotesPdf';
import { useMemo, useState } from 'react';
import Materials from './components/materials';
import AxisMaterials, {
  ILessonAxisMaterial,
} from './components/axis-materials';
import Podcasts from './components/podcasts';
import { EmptyState } from '../../../../modules/Practice/components/EmptyState';
import ExercisesContent from './components/exercises';
import FreemiumLockContent from '../FreemiumLockModal/LockContent';
import { openFreemiumUpsell } from '../../../stores/freemiumUpsellStore';
import { useLessonProgressLog } from '../../../hooks/useLessonProgressLog';
import { ViewOrigin } from '../../../models/contentView';

interface Subject {
  title: string;
  _id: string;
}

interface ILesson {
  id: string;
  slug?: string;
  title: string;
  module?: string;
  discipline: string;
  subjects?: string | Subject[];
  subjectRelated?: string;
  time?: string | number;
  teacher: string;
  pdfSummary?: string;
  notesPdfUri?: string | null;
  captionUri?: string;
  review?: 'like' | 'dislike' | undefined;
  isFavorite?: boolean;
  timeline: Array<any>;
  playerType: PlayerType;
  token: string;
  embedUrl?: string;
  defaultPlayer?: PlayerType;
  breadcrumb?: Array<BreadcrumbsItem>;
  timelineText?: string;
  /**
   * `origin` diz se a conclusão veio do fim real do vídeo (`ended`) ou do corte
   * que já o considera assistido (`virtual`, por volta de 80%). Quem não
   * precisa da distinção pode ignorar o parâmetro.
   */
  onFinish?: (origin?: 'virtual' | 'ended') => any;
  virtualFinishAt?: number;
  showFavorite?: boolean;
  contentSlug?: string;
  /**
   * De onde a aula está sendo assistida. Vai junto do log de progresso para o
   * relatório separar navegação livre, cursos e cronograma.
   */
  viewOrigin?: ViewOrigin;
  tags?: TagProps[];
  recordedAt?: string;
  contentType?: 'Lesson' | 'Material' | 'Live';
  isDisciplines?: boolean;
  isLiveStream?: boolean;
  showLiveChat?: boolean;
  showQuiz?: boolean;
  courseId?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  locked?: boolean;
  isFreemiumUser?: boolean;
  /**
   * Conteúdo opcional exibido ao lado dos comentários (coluna à direita) quando
   * `isDisciplines` é falso. Usado, por ex., pela playlist de "Conexões com
   * Jubilut". Sem valor, os comentários seguem ocupando a largura toda.
   */
  sidebar?: React.ReactNode;
  /**
   * Ação opcional exibida centralizada entre os botões "Voltar" e "Próxima"
   * (ex.: "Escrever sobre o tema" em Conexões com Jubilut). Sem valor, a linha
   * mantém apenas os dois botões de navegação.
   */
  centerAction?: React.ReactNode;
  /**
   * Materiais vinculados por eixo temático (Conexões com Jubilut), exibidos na
   * aba "Materiais" ao lado dos materiais da matéria (quando `isDisciplines` é
   * falso). Sem valor, a aba mostra apenas os materiais relacionados à matéria.
   */
  axisMaterials?: ILessonAxisMaterial[];
}

export default function Lesson({
  id,
  title,
  discipline,
  time,
  teacher,
  pdfSummary,
  notesPdfUri,
  captionUri,
  timeline = [],
  timelineText,
  token,
  playerType,
  embedUrl,
  defaultPlayer,
  breadcrumb = [],
  isFavorite,
  review,
  onFinish,
  virtualFinishAt,
  showFavorite = true,
  contentSlug,
  viewOrigin,
  tags = [],
  recordedAt,
  isLiveStream = false,
  showLiveChat = false,
  isDisciplines = false,
  contentType,
  subjects,
  subjectRelated,
  module,
  showQuiz = false,
  courseId,
  onNext,
  onPrevious,
  locked = false,
  isFreemiumUser = false,
  sidebar,
  centerAction,
  axisMaterials = [],
}: ILesson) {
  const subject = Array.isArray(subjects) ? subjects?.at(0) : undefined;

  // Log de progresso: o player informa os segundos, o backend decide se a aula
  // foi iniciada ou consumida.
  const reportarProgresso = useLessonProgressLog({
    slug: contentSlug,
    origin: viewOrigin,
    course: courseId,
  });

  const {
    isQuizOpen,
    setIsQuizOpen,
    seekTo,
    activeTab,
    setActiveTab,
    showModalScore,
    setShowModalScore,
    quiz,
    relatedData,
    isRelatedPending,
    flashcardsFiltered,
    isLoadingFlashcards,
    fetchFlashcards,
    timeLesson,
    showFlashcards,
    hasQuiz,
    isLive,
    commentTargetType,
    scoreProps,
    handleFinish,
    handleVideoEnd,
    changeFlashcardVinculed,
    updateRelatedFavorite,
    updateRelatedDone,
    dispatchEventTrackerPdfSummary,
    onChangeTimeline,
  } = useLessonPage({
    id,
    time,
    subjectRelated,
    lessonSlug: contentSlug,
    module,
    courseId,
    isLiveStream,
    contentType,
    showQuiz,
    onFinish,
  });

  const {
    openNotesPdf,
    isLoading: isNotesPdfLoading,
    isReady: isNotesPdfReady,
  } = useLessonNotesPdf(id, !isLiveStream, notesPdfUri);
  const [selectedFilter, setSelectedFilter] = useState<string>('comments');

  const commentElement = isFreemiumUser ? (
    <FreemiumLockContent
      showButton={false}
      title='Comentários exclusivos para assinantes'
      description='Os comentários das videoaulas estão disponíveis apenas para alunos assinantes.'
      className='max-w-[520px]'
    />
  ) : (
    <Comment
      title='Comentários da videoaula'
      targetId={id}
      targetType={commentTargetType}
    />
  );

  const flashcardsLockedElement = (
    <FreemiumLockContent
      showButton={false}
      title='Flashcards exclusivos para assinantes'
      description='Os flashcards das videoaulas estão disponíveis apenas para alunos assinantes.'
      className='max-w-[520px]'
    />
  );

  const flashcardsElement = isFreemiumUser
    ? flashcardsLockedElement
    : showFlashcards && (
        <LessonFlashcards
          flashcards={flashcardsFiltered}
          isLoading={isLoadingFlashcards}
          subject={subject}
          onSuccessFallback={changeFlashcardVinculed}
        />
      );

  const materialsElement = (
    <Materials
      discipline={discipline}
      module={module}
      data={relatedData}
      isPending={isRelatedPending}
      onFavoriteCallback={updateRelatedFavorite}
      onDone={(id, slug) => updateRelatedDone(id, slug)}
    />
  );

  // Aba "Materiais" (player sem Disciplinas, ex.: Conexões com Jubilut): combina
  // os materiais do eixo temático com os materiais da matéria da aula.
  const hasSubjectMaterials = (relatedData?.workbooks?.length ?? 0) > 0;
  const showMaterialsTab = axisMaterials.length > 0 || hasSubjectMaterials;

  const materialsTabElement = (
    <div className='flex flex-col gap-xxl'>
      <AxisMaterials materials={axisMaterials} />
      {hasSubjectMaterials && (
        <Materials
          discipline={discipline}
          module={module}
          data={relatedData}
          isPending={isRelatedPending}
          onFavoriteCallback={updateRelatedFavorite}
          onDone={(id, slug) => updateRelatedDone(id, slug)}
          title='DA MATÉRIA'
        />
      )}
    </div>
  );

  const podcastsElement = (
    <Podcasts
      discipline={discipline}
      module={module}
      data={relatedData}
      isPending={isRelatedPending}
      onFavoriteCallback={updateRelatedFavorite}
      onDone={(id, slug) => updateRelatedDone(id, slug)}
    />
  );

  const exercisesElement = (
    <ExercisesContent
      discipline={discipline}
      module={module}
      data={relatedData}
      isPending={isRelatedPending}
      onFavoriteCallback={updateRelatedFavorite}
      // "Marcar exercício como concluído" desabilitado temporariamente.
      // O botão continua visível, porém sem ação (no-op abaixo).
      // Para reativar (implementação futura), basta trocar o no-op pelo handler:
      // onDone={(id, slug) => updateRelatedDone(id, slug)}
      onDone={() => {}}
    />
  );

  const renderContent = useMemo(() => {
    const emptyMessages: Record<string, { text: string; subtext: string }> = {
      comments: {
        text: 'Nenhum comentário encontrado',
        subtext: 'Seja o primeiro a comentar nesta videoaula.',
      },
      flashcards: {
        text: 'Nenhum flashcard encontrado',
        subtext: 'Não há flashcards disponíveis para esta videoaula.',
      },
      materials: {
        text: 'Nenhum material encontrado',
        subtext: 'Não há materiais disponíveis para esta videoaula.',
      },
      podcasts: {
        text: 'Nenhum podcast encontrado',
        subtext: 'Não há podcasts disponíveis para esta videoaula.',
      },
      exercises: {
        text: 'Nenhum exercício encontrado',
        subtext: 'Não há exercícios disponíveis para esta videoaula.',
      },
    };

    const isEmpty: Record<string, boolean> = {
      flashcards:
        !isFreemiumUser &&
        !isLoadingFlashcards &&
        flashcardsFiltered.length === 0,
      materials:
        !isRelatedPending &&
        (!relatedData?.workbooks || relatedData.workbooks.length === 0),
      podcasts:
        !isRelatedPending &&
        (!relatedData?.podcast || relatedData.podcast.length === 0),
      exercises:
        !isRelatedPending &&
        (!relatedData?.exercise || relatedData.exercise.length === 0),
    };

    const types = {
      comments: commentElement,
      flashcards: flashcardsElement,
      materials: materialsElement,
      podcasts: podcastsElement,
      exercises: exercisesElement,
    };

    const content = types[selectedFilter as keyof typeof types];

    if (selectedFilter !== 'comments' && isEmpty[selectedFilter]) {
      return <EmptyState {...emptyMessages[selectedFilter]} />;
    }

    return content;
  }, [
    selectedFilter,
    flashcardsFiltered,
    isLoadingFlashcards,
    relatedData,
    isRelatedPending,
    isFreemiumUser,
  ]);

  return (
    <div>
      <LessonHeader
        title={title}
        discipline={discipline}
        breadcrumb={breadcrumb}
        tags={tags}
        subjects={subjects}
        timeLesson={timeLesson}
        teacher={teacher}
        recordedAt={recordedAt}
        id={id}
        contentSlug={contentSlug}
        contentType={contentType}
        isLiveStream={isLiveStream}
        review={review}
        isFavorite={isFavorite}
        showFavorite={showFavorite}
        scoreProps={scoreProps}
      />

      {locked ? (
        <div className='bg-color-light flex aspect-video w-full items-center justify-center rounded-md py-8'>
          <FreemiumLockContent className='max-w-[520px]' />
        </div>
      ) : (
        <LessonPlayer
          id={id}
          discipline={discipline}
          title={title}
          token={token}
          playerType={playerType}
          embedUrl={embedUrl}
          defaultPlayer={defaultPlayer}
          seekTo={seekTo}
          onFinish={handleFinish}
          virtualFinishAt={virtualFinishAt}
          onVideoEnd={handleVideoEnd}
          onProgressSeconds={reportarProgresso}
          isLiveStream={isLiveStream}
          showLiveChat={showLiveChat}
          quiz={quiz}
          isQuizOpen={isQuizOpen}
          setIsQuizOpen={setIsQuizOpen}
          showQuizFeature={showFlashcards}
          captionUri={captionUri}
        />
      )}

      <Timeline
        items={timeline}
        label={timelineText}
        onClickItem={onChangeTimeline}
      />

      {(onPrevious || onNext || centerAction) && (
        <div className='flex items-center justify-between gap-4 pt-4'>
          <Button
            variant='outlined'
            onClick={onPrevious}
            disabled={!onPrevious}
            className='min-w-[166px] justify-center'
          >
            Voltar
          </Button>
          {centerAction && (
            <div className='flex flex-1 justify-center'>{centerAction}</div>
          )}
          <Button
            variant='contained'
            onClick={onNext}
            disabled={!onNext}
            className='min-w-[166px] justify-center'
          >
            Próxima
          </Button>
        </div>
      )}

      {hasQuiz && (
        <div className='flex justify-center pt-8 md:hidden'>
          <Button
            variant='outlined'
            LeftIcon='quiz'
            onClick={() => setIsQuizOpen(true)}
            full
          >
            Responda ao quiz da aula
          </Button>
        </div>
      )}

      {/*{pdfSummary && (*/}
      <div className='flex justify-end pt-8'>
        {isFreemiumUser ? (
          <Button
            LeftIcon='open_in_new'
            variant='text'
            onClick={() => openFreemiumUpsell()}
          >
            {isLiveStream || contentType === 'Live'? (
              pdfSummary && (
                <a href={pdfSummary} target='_blank' rel='noreferrer'>
                  <Button
                    LeftIcon='open_in_new'
                    variant='text'
                    onClick={dispatchEventTrackerPdfSummary}
                  >
                    Abrir material
                  </Button>
                </a>
              )
            ) : (
              <Button
                LeftIcon='open_in_new'
                variant='text'
                loading={isNotesPdfLoading}
                disabled={!isNotesPdfReady}
                onClick={() => {
                  dispatchEventTrackerPdfSummary();
                  openNotesPdf();
                }}
              >
                Anotações da aula
              </Button>
            )}
          </Button>
        ) : isLiveStream || contentType === 'Live' ? (
          pdfSummary && (
            <a href={pdfSummary} target='_blank' rel='noreferrer'>
              <Button
                LeftIcon='open_in_new'
                variant='text'
                onClick={dispatchEventTrackerPdfSummary}
              >
                Abrir material
              </Button>
            </a>
          )
        ) : (
          <Button
            LeftIcon='open_in_new'
            variant='text'
            loading={isNotesPdfLoading}
            disabled={!isNotesPdfReady}
            onClick={() => {
              dispatchEventTrackerPdfSummary();
              openNotesPdf();
            }}
          >
            Anotações da aula
          </Button>
        )}
      </div>
      {/*)}*/}

      {!isLiveStream && isDisciplines && (
        <div className='pb-16 pt-8'>
          <div className='flex lg:hidden'>
            <ButtonGroup
              className='mb-12 w-full'
              buttons={[
                {
                  label: 'Conteúdos relacionados',
                  onClick: () => setActiveTab('related'),
                  selected: activeTab === 'related',
                },
                {
                  label: 'Comentários',
                  onClick: () => setActiveTab('comments'),
                  selected: activeTab === 'comments',
                },
              ]}
            />
          </div>

          <div className='block lg:hidden'>
            {activeTab === 'related' && (
              <>
                <Typography
                  type='title'
                  size='h3'
                  className='text-mono-color-black'
                >
                  Conteúdos relacionados à videoaula
                </Typography>
                <RelatedContent
                  discipline={discipline}
                  module={module}
                  data={relatedData}
                  isPending={isRelatedPending}
                  onFavoriteCallback={updateRelatedFavorite}
                  onDone={(id, slug) => updateRelatedDone(id, slug)}
                />
                {flashcardsElement}

                <NextVideo />
              </>
            )}

            {activeTab === 'comments' && commentElement}
          </div>

          <div className='hidden gap-20 md:flex-row lg:flex'>
            <div className='flex min-w-0 flex-1 flex-col gap-4'>
              <TagsFiltersLesson
                discipline={discipline as Colors}
                selectedFilter={selectedFilter}
                onChangeFilter={setSelectedFilter}
              />
              <div className='hidden lg:block'>{renderContent}</div>
            </div>
            <div className='w-[380px] shrink-0'>
              <NextVideo />
            </div>
          </div>
        </div>
      )}

      {!isLiveStream && !isDisciplines && (
        <div className='pb-16 pt-8'>
          <div
            className={
              sidebar
                ? 'flex flex-col gap-xxl lg:flex-row lg:items-start'
                : undefined
            }
          >
            <div className='min-w-0 flex-1'>
              {!isLive && (
                <LessonTabs
                  activeTab={activeTab}
                  onSelectTab={setActiveTab}
                  showMaterials={showMaterialsTab}
                />
              )}

              {activeTab === 'comments' && commentElement}

              {activeTab === 'flashcards' &&
                (isFreemiumUser
                  ? flashcardsLockedElement
                  : showFlashcards && (
                      <LessonFlashcards
                        flashcards={flashcardsFiltered}
                        isLoading={isLoadingFlashcards}
                        subject={subject}
                        onSuccessFallback={() => fetchFlashcards(id)}
                        title='Flashcards da videoaula'
                        emptyMessage='Nenhum flashcard encontrado'
                        gridClassName='grid grid-cols-3 gap-xxxl'
                      />
                    ))}

              {activeTab === 'materials' &&
                showMaterialsTab &&
                materialsTabElement}
            </div>

            {sidebar && <div className='shrink-0 lg:w-[380px]'>{sidebar}</div>}
          </div>
        </div>
      )}

      {showModalScore && (
        <PopUpModal
          isOpen={showModalScore}
          onClose={() => setShowModalScore(false)}
          score={5}
        />
      )}
    </div>
  );
}

const LessonTabs = ({
  activeTab,
  onSelectTab,
  showMaterials = false,
}: {
  activeTab: string;
  onSelectTab: (value: 'comments' | 'flashcards' | 'materials') => void;
  showMaterials?: boolean;
}) => {
  const classSelected =
    'bg-color-brand-primary border-primary-color-default !text-card-color-default';

  const tabs: {
    label: string | React.ReactNode;
    value: 'comments' | 'flashcards' | 'materials';
  }[] = [
    { label: 'Comentários', value: 'comments' },
    { label: <i>Flashcards</i>, value: 'flashcards' },
    ...(showMaterials
      ? [{ label: 'Materiais', value: 'materials' as const }]
      : []),
  ];

  return (
    <div className='flex gap-2 pb-4'>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          className={cnBase(
            'rounded rounded-pill border-hairline border-color-brand-primary px-4 py-1 text-color-brand-primary',
            activeTab === tab.value && classSelected
          )}
          onClick={() => onSelectTab(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};
